import { clearMessage, showError, showMessage } from "./Messages.svelte";

import { getGitHubToken } from "./github_login";
import { logGistEvent } from "./events.js";
import { startTimer } from "./util.js";

export function addHeader(opts: any, key: string, val: string): void {
  opts.headers = opts.headers || {};
  opts.headers[key] = val;
}

export function addToken(opts: any): any {
  const token = getGitHubToken();
  if (token) {
    addHeader(opts, "Authorization", "token " + token);
  }
  return opts;
}

export function checkResponse(rsp: Response, method: string, uri: string): boolean {
  if (rsp.ok) {
    return true;
  }
  showError(`${method} ${uri} failed with ${rsp.status}`);
  return false;
}

export async function apiGet(endpoint: string): Promise<any> {
  const uri = `https://api.github.com${endpoint}`;
  try {
    const opts = addToken({});
    // TODO: breaks CORS
    // addHeader(opts, "X-GitHub-Api-Version", "2022-11-28");
    addHeader(opts, "Accept", "application/vnd.github+json");

    const rsp = await fetch(uri, opts);
    if (!checkResponse(rsp, "GET", uri)) {
      return null;
    }
    const js = await rsp.json();
    // console.log("github.apiGet(", endpoint, ") js:", js);
    clearMessage();
    return js;
  } catch (ex) {
    showError(`GET ${uri} failed with '${ex}`);
  }
  return null;
}

// <https://api.github.com/gists?per_page=100&page=2>; rel="next", <https://api.github.com/gists?per_page=100&page=4>; rel="last"
export function getLinkNext(link: string): string {
  link = link || "";
  let m = link.match(/<([^>]+)>;\s*rel="next"/);
  return (m || [])[1];
}

export async function apiGetPaginateAll(endpoint: string): Promise<any[] | null> {
  const res: any[] = [];
  let uri: string = `https://api.github.com${endpoint}`;
  try {
    const opts = addToken({});
    while (uri) {
      const rsp = await fetch(uri, opts);
      if (!checkResponse(rsp, "GET", uri)) {
        return null;
      }
      const js = await rsp.json();
      for (let el of js) {
        res.push(el);
      }
      uri = "";
      const link = rsp.headers.get("Link");
      if (link) {
        uri = getLinkNext(link);
      }
    }
    // console.log("github.apiGet(", endpoint, ") js:", js);
    clearMessage();
    return res;
  } catch (ex) {
    showError(`GET ${uri} failed with '${ex}`);
  }
  return null;
}

export async function apiDelete(endpoint: string): Promise<boolean> {
  const uri = `https://api.github.com${endpoint}`;
  try {
    let opts: any = {
      method: "DELETE",
    };
    opts = addToken(opts);
    const rsp = await fetch(uri, opts);
    if (!checkResponse(rsp, "DELETE", uri)) {
      return false;
    }
    // console.log("github.apiDelete(", endpoint, ")");
    clearMessage();
  } catch (ex) {
    showError(`DELETE ${uri} failed with '${ex}`);
    return false;
  }
  return true;
}

export async function apiPatch(endpoint: string, data: any): Promise<any> {
  const uri = `https://api.github.com${endpoint}`;
  try {
    let opts: any = {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    };
    opts = addToken(opts);
    const rsp = await fetch(uri, opts);
    if (!checkResponse(rsp, "PATCH", uri)) {
      return null;
    }
    const js = await rsp.json();
    // console.log("github.apiPatch(", endpoint, ") js:", js);
    clearMessage();
    return js;
  } catch (ex) {
    showError(`PATCH api.github.com${endpoint} failed with '${ex}`);
  }
  return null;
}

export async function apiPost(endpoint: string, data: any): Promise<any> {
  const uri = `https://api.github.com${endpoint}`;
  try {
    let opts: any = {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    };
    opts = addToken(opts);
    const rsp = await fetch(uri, opts);
    if (!checkResponse(rsp, "POST", uri)) {
      return null;
    }
    const js = await rsp.json();
    // console.log("github.apiPost(", endpoint, ") js:", js);
    clearMessage();
    return js;
  } catch (ex) {
    showError(`POST api.github.com${endpoint} failed with '${ex}`);
  }
  return null;
}

export function checkForError(rsp: any): any {
  if (!rsp) {
    return rsp;
  }
  if (rsp.errors) {
    const msg = rsp.message;
    showError(msg);
    return null;
  }
  return rsp;
}

/*
// https://developer.github.com/v3/gists/#list-a-users-gists
export async function getGistsForUser(userID) {
  showMessage(`getting list of gists for user ${userID}`, 10000);
  const elapsedFn = startTimer();
  const uri = `/users/${userID}/gists?per_page=100`;
  const gists = await apiGetPaginateAll(uri);
  const ev = {
    user: userID,
  };
  logGistEvent("getGistsForUser", elapsedFn(), ev)
  console.log(`getGistsForUser: '${userID} ${len(gists)} in ${elapsedFn()} ms`);
  return checkForError(gists);
}
*/

// https://developer.github.com/v3/users/#get-the-authenticated-user
export async function getLoggedUserInfo(): Promise<any> {
  showMessage(`getting github user info`, 10000);
  const um = await apiGet("/user");
  return checkForError(um);
}

// https://developer.github.com/v3/gists/#edit-a-gist
export async function updateGist(gistId: string, data: Record<string, any>): Promise<any> {
  const elapsedFn = startTimer();
  showMessage(`saving the gist`, 10000);
  const uri = `/gists/${gistId}`;
  const gist = await apiPatch(uri, data);
  const meta = {
    gistId: gistId,
  };
  logGistEvent("updateGist", elapsedFn(), meta);
  return checkForError(gist);
}

// https://developer.github.com/v3/gists/#create-a-gist
export async function createGist(gist: Record<string, any>): Promise<any> {
  const elapsedFn = startTimer();
  console.log("createGist:", gist);
  showMessage(`creating a new gist`, 10000);
  const res = await apiPost(`/gists`, gist);
  logGistEvent("createGist", elapsedFn());
  return checkForError(res);
}

// https://developer.github.com/v3/gists/#delete-a-gist
export async function deleteGist(gistId: string): Promise<boolean> {
  console.log(`deleteGist ${gistId}`);

  showMessage("deleting the gist", 10000);
  const elapsedFn = startTimer();
  const ok = await apiDelete(`/gists/${gistId}`);
  const meta = {
    gistId: gistId,
  };
  logGistEvent("deleteGist", elapsedFn(), meta);
  return ok;
}
